/**
 * Stock class operations service.
 * Provides async functions for stock class operations.
 */

import { queryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import { AnimalGroup, StockClass } from "model/animalGroupTypes";

/**
 * Fetch stock classes by IDs.
 */
export async function fetchstockClassesById(
  stockClassIds: string[]
): Promise<StockClass[]> {
  if (!stockClassIds || stockClassIds.length === 0) {
    return [];
  }
  return fetchApi(`stockClasses?ids=${stockClassIds.join(",")}`) as Promise<
    StockClass[]
  >;
}

// Stock class birthing period type
interface StockClassBirthingPeriod {
  guid: string;
  stockClass: string;
  dateStart: string;
  dateEnd: string;
  numberOfBirths: number;
}

/**
 * Create a stock class birthing period.
 */
export async function createStockClassBirthingPeriod(
  birthingPeriod: Partial<StockClassBirthingPeriod>
): Promise<StockClassBirthingPeriod> {
  return fetchApi("stockClassBirthingPeriods", {
    method: "POST",
    body: JSON.stringify(birthingPeriod),
  }) as Promise<StockClassBirthingPeriod>;
}

/**
 * Update a stock class birthing period.
 */
export async function updateStockClassBirthingPeriod(
  birthingPeriod: StockClassBirthingPeriod
): Promise<StockClassBirthingPeriod> {
  return fetchApi(`stockClassBirthingPeriods/${birthingPeriod.guid}`, {
    method: "PUT",
    body: JSON.stringify(birthingPeriod),
  }) as Promise<StockClassBirthingPeriod>;
}

/**
 * Delete a stock class birthing period.
 */
export async function deleteStockClassBirthingPeriod(
  birthingPeriodId: string
): Promise<void> {
  await fetchApi(`stockClassBirthingPeriods/${birthingPeriodId}`, {
    method: "DELETE",
  });
}

/**
 * Create a new stock class.
 */
export async function createStockClass(
  stockClass: Partial<StockClass>
): Promise<StockClass> {
  const result = (await fetchApi("stockClasses", {
    method: "POST",
    body: JSON.stringify(stockClass),
  })) as StockClass;
  const created = (await fetchApi(`stockClasses/${result.guid}`)) as StockClass;
  queryClient.setQueryData(
    queryKeys.stockClasses.detail(created.guid),
    created
  );
  if (created.animalGroup) {
    const ag = queryClient.getQueryData<AnimalGroup>(
      queryKeys.animalGroups.detail(created.animalGroup)
    );
    if (ag && !ag.stockClasses.includes(created.guid)) {
      queryClient.setQueryData(
        queryKeys.animalGroups.detail(created.animalGroup),
        { ...ag, stockClasses: [...ag.stockClasses, created.guid] }
      );
    }
  }
  return created;
}

/**
 * Update a stock class.
 */
export async function updateStockClass(
  stockClass: StockClass
): Promise<StockClass> {
  await fetchApi(`stockClasses/${stockClass.guid}`, {
    method: "PUT",
    body: JSON.stringify(stockClass),
  });
  const updated = (await fetchApi(
    `stockClasses/${stockClass.guid}`
  )) as StockClass;
  queryClient.setQueryData(
    queryKeys.stockClasses.detail(updated.guid),
    updated
  );
  return updated;
}

/**
 * Delete a stock class.
 */
export async function deleteStockClass(stockClassId: string): Promise<void> {
  await fetchApi(`stockClasses/${stockClassId}`, {
    method: "DELETE",
  });
}
